import crossCompanyMirrorConfigEndpoints from '@/lib/api/endpoints/crossCompanyMirrorConfigEndpoints';
import type {
	CrossCompanyMirrorConfig,
	CrossCompanyWorker,
	CrossCompanyJobOrder,
	CrossCompanyJobActivity,
} from '@/lib/models/crossCompanyMirrorConfig';
import {
	CrossCompanyMirrorConfigSchema,
	CrossCompanyWorkerSchema,
	CrossCompanyJobOrderSchema,
	CrossCompanyJobActivitySchema,
} from '@/lib/models/crossCompanyMirrorConfig';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';
import modulesConfiguration from '@/lib/constants/modulesConfiguration';

/**
 * Verifica che il modulo CrossCompanyMirroring sia abilitato
 * @throws Error se il modulo è disabilitato
 */
function checkFeatureEnabled(): void {
	if (!modulesConfiguration.crossCompanyMirroring) {
		throw new Error('CrossCompanyMirroring module is disabled');
	}
}

/**
 * Interfaccia per il service CrossCompanyMirrorConfig
 * Gestisce la logica di business e la validazione runtime per le configurazioni cross-company
 */
interface CrossCompanyMirrorConfigService {
	/**
	 * Recupera lista paginata di configurazioni con query OData
	 * @param query - Query string OData
	 * @returns Promise con ODataResponse di CrossCompanyMirrorConfig
	 */
	getCrossCompanyMirrorConfigs(query: string): Promise<ODataResponse<CrossCompanyMirrorConfig>>;

	/**
	 * Recupera una singola configurazione per ID
	 * @param id - ID della configurazione
	 * @returns Promise con CrossCompanyMirrorConfig
	 */
	getCrossCompanyMirrorConfig(id: number): Promise<CrossCompanyMirrorConfig>;

	/**
	 * Crea una nuova configurazione
	 * @param payload - Dati della configurazione
	 * @returns Promise con CrossCompanyMirrorConfig creata
	 */
	postCrossCompanyMirrorConfig(
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<CrossCompanyMirrorConfig>;

	/**
	 * Aggiorna una configurazione esistente
	 * @param id - ID della configurazione
	 * @param payload - Dati da aggiornare
	 * @returns Promise con CrossCompanyMirrorConfig aggiornata
	 */
	updateCrossCompanyMirrorConfig(
		id: number,
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<CrossCompanyMirrorConfig>;

	/**
	 * Elimina una configurazione
	 * @param id - ID della configurazione da eliminare
	 * @returns Promise void
	 */
	deleteCrossCompanyMirrorConfig(id: number): Promise<void>;

	/**
	 * Recupera lista delle company target disponibili
	 * @returns Promise con array di company target
	 */
	getTargetCompanies(): Promise<string[]>;

	/**
	 * Recupera workers della company target
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con array di CrossCompanyWorker
	 */
	getCrossCompanyWorkers(targetCompany: string, query: string): Promise<CrossCompanyWorker[]>;

	/**
	 * Recupera job orders della company target
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con array di CrossCompanyJobOrder
	 */
	getCrossCompanyJobOrders(targetCompany: string, query: string): Promise<CrossCompanyJobOrder[]>;

	/**
	 * Recupera job activities della company target
	 * @param targetCompany - Company target
	 * @param query - Query string
	 * @returns Promise con array di CrossCompanyJobActivity
	 */
	getCrossCompanyJobActivities(
		targetCompany: string,
		query: string,
	): Promise<CrossCompanyJobActivity[]>;
}

/**
 * Implementazione del service CrossCompanyMirrorConfig
 * Mantiene compatibilità con il JavaScript originale e aggiunge validazione runtime
 */
const crossCompanyMirrorConfigService: CrossCompanyMirrorConfigService = {
	async getCrossCompanyMirrorConfigs(
		query: string,
	): Promise<ODataResponse<CrossCompanyMirrorConfig>> {
		checkFeatureEnabled();
		const response = (await crossCompanyMirrorConfigEndpoints.getCrossCompanyMirrorConfigs(query))
			.data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateODataResponse(
			response,
			CrossCompanyMirrorConfigSchema,
			'CrossCompanyMirrorConfig.getAll',
		);
		return response;
	},

	async getCrossCompanyMirrorConfig(id: number): Promise<CrossCompanyMirrorConfig> {
		checkFeatureEnabled();
		const response = (await crossCompanyMirrorConfigEndpoints.getCrossCompanyMirrorConfig(id)).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(
			response,
			CrossCompanyMirrorConfigSchema,
			'CrossCompanyMirrorConfig.getById',
		);
		return response;
	},

	async postCrossCompanyMirrorConfig(
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<CrossCompanyMirrorConfig> {
		checkFeatureEnabled();
		const response = (await crossCompanyMirrorConfigEndpoints.postCrossCompanyMirrorConfig(payload))
			.data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(
			response,
			CrossCompanyMirrorConfigSchema,
			'CrossCompanyMirrorConfig.create.response',
		);
		return response;
	},

	async updateCrossCompanyMirrorConfig(
		id: number,
		payload: Partial<CrossCompanyMirrorConfig>,
	): Promise<CrossCompanyMirrorConfig> {
		checkFeatureEnabled();
		const response = (
			await crossCompanyMirrorConfigEndpoints.updateCrossCompanyMirrorConfig(id, payload)
		).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateSingle(
			response,
			CrossCompanyMirrorConfigSchema,
			'CrossCompanyMirrorConfig.update.response',
		);
		return response;
	},

	async deleteCrossCompanyMirrorConfig(id: number): Promise<void> {
		checkFeatureEnabled();
		return (await crossCompanyMirrorConfigEndpoints.deleteCrossCompanyMirrorConfig(id)).data;
	},

	async getTargetCompanies(): Promise<string[]> {
		checkFeatureEnabled();
		const response = (await crossCompanyMirrorConfigEndpoints.getTargetCompanies()).data;
		// Note: Simple string array, no schema validation needed
		return response;
	},

	async getCrossCompanyWorkers(
		targetCompany: string,
		query: string,
	): Promise<CrossCompanyWorker[]> {
		checkFeatureEnabled();
		const response = (
			await crossCompanyMirrorConfigEndpoints.getCrossCompanyWorkers(targetCompany, query)
		).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateArray(
			response,
			CrossCompanyWorkerSchema,
			'CrossCompanyWorker.getCrossCompanyWorkers',
		);
		return response;
	},

	async getCrossCompanyJobOrders(
		targetCompany: string,
		query: string,
	): Promise<CrossCompanyJobOrder[]> {
		checkFeatureEnabled();
		const response = (
			await crossCompanyMirrorConfigEndpoints.getCrossCompanyJobOrders(targetCompany, query)
		).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateArray(
			response,
			CrossCompanyJobOrderSchema,
			'CrossCompanyJobOrder.getCrossCompanyJobOrders',
		);
		return response;
	},

	async getCrossCompanyJobActivities(
		targetCompany: string,
		query: string,
	): Promise<CrossCompanyJobActivity[]> {
		checkFeatureEnabled();
		const response = (
			await crossCompanyMirrorConfigEndpoints.getCrossCompanyJobActivities(targetCompany, query)
		).data;
		// Validazione runtime della response (attiva solo in development)
		zodValidator.validateArray(
			response,
			CrossCompanyJobActivitySchema,
			'CrossCompanyJobActivity.getCrossCompanyJobActivities',
		);
		return response;
	},
};

export default crossCompanyMirrorConfigService;
